import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  User,
  Users,
  CalendarCheck,
  Pill,
  Stethoscope,
  BedDouble,
  LogOut,
  Phone,
  FlaskRound,
  ArrowLeft
} from "lucide-react";

const AllActivities = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper: Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("session_token") || localStorage.getItem("token") || null;
  };

  // Helper: Append token to URL
  const appendTokenToUrl = (url) => {
    const token = getToken();
    if (!token) return url;
    
    try {
      const urlObj = new URL(url);
      if (!urlObj.searchParams.has("token")) {
        urlObj.searchParams.append("token", token);
      }
      return urlObj.toString();
    } catch (error) {
      // If URL parsing fails, append manually
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}token=${encodeURIComponent(token)}`;
    }
  };

  // Helper: Get auth headers
  const getAuthHeaders = () => {
    const token = getToken();
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      headers["X-Session-Token"] = token;
    }
    
    return headers;
  };

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const apiUrl = appendTokenToUrl("https://newerp.awasthivitality.com/api/dashboard/all-recent-activities");
        
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        const data = await response.json();
        
        if (data.success && data.data) {
          // Format activities with time display
          const formattedActivities = data.data.map((activity) => {
            const timestamp = activity.timestamp || activity.created_at;
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            let timeDisplay = "";
            if (diffMins < 1) {
              timeDisplay = "Just now";
            } else if (diffMins < 60) {
              timeDisplay = `${diffMins}m ago`;
            } else if (diffHours < 24) {
              timeDisplay = `${diffHours}h ago`;
            } else if (diffDays < 7) {
              timeDisplay = `${diffDays}d ago`;
            } else {
              timeDisplay = date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              });
            }

            return {
              ...activity,
              time: timeDisplay,
              status: activity.action || activity.type || "Activity"
            };
          });

          setActivities(formattedActivities);
        } else {
          setActivities([]);
        }
      } catch (err) {
        console.error("Error fetching activities:", err);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, []);

  const getIcon = (text) => {
    text = text.toLowerCase();
    if (text.includes("patient")) return User;
    if (text.includes("lead")) return Users;
    if (text.includes("appointment")) return CalendarCheck;
    if (text.includes("medicine") || text.includes("drug")) return Pill;
    if (text.includes("doctor") || text.includes("consult")) return Stethoscope;
    if (text.includes("admit")) return BedDouble;
    if (text.includes("discharge")) return LogOut;
    if (text.includes("follow")) return Phone;
    if (text.includes("test") || text.includes("lab")) return FlaskRound;
    return FileText;
  };

  const colors = ["#3b82f6", "#22c55e", "#a855f7", "#f97316", "#ec4899"];

  return (
    <div className="activities-wrapper" style={{ background: "#f8fafc", minHeight: "100vh", padding: "16px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          marginBottom: "16px"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <ArrowLeft
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}
          />
          <h2 style={{ margin: 0, fontSize: "20px" }}>All Activities</h2>
        </div>

        <span
          style={{
            background: "#e0f2fe",
            color: "#0369a1",
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "13px",
            whiteSpace: "nowrap"
          }}
        >
          Total: {activities.length}
        </span>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <div className="spinner"></div>
          <p style={{ marginTop: "10px", color: "#64748b" }}>Loading activities...</p>
        </div>
      )}

      {/* Activities */}
      <div className="activities-grid">
        {!loading && activities.length > 0 ? (
          activities.map((activity, index) => {
            const Icon = getIcon(
              `${activity.status} ${activity.description}`
            );

            const color = colors[index % colors.length];

            return (
              <div key={index} className="activity-card">
                <div className="activity-card-inner">
                  {/* Icon */}
                  <div
                    className="activity-icon"
                    style={{ background: color + "20" }}
                  >
                    <Icon size={22} color={color} />
                  </div>

                  {/* Content */}
                  <div className="activity-content">
                    <div className="activity-time">
                      {activity.time}
                    </div>

                    <div className="activity-title">
                      {activity.status}
                    </div>

                    <div className="activity-desc">
                      {activity.description}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : !loading ? (
          <p className="no-activities">No activities found.</p>
        ) : null}
      </div>

      {/* Styles */}
      <style>
        {`
          /* Grid Responsive */
          .activities-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 12px;
          }

          @media (min-width: 640px) {
            .activities-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }

          @media (min-width: 1024px) {
            .activities-grid {
              grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
            }
          }

          /* Card */
          .activity-card {
            background: #fff;
            border-radius: 16px;
            padding: 14px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.04);
            transition: 0.25s ease;
            cursor: pointer;
          }

          .activity-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 12px 30px rgba(0,0,0,0.08);
          }

          /* Card Inner */
          .activity-card-inner {
            display: flex;
            align-items: flex-start;
            gap: 12px;
          }

          /* Icon */
          .activity-icon {
            width: 48px;
            height: 48px;
            min-width: 48px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          /* Content */
          .activity-content {
            flex: 1;
          }

          .activity-time {
            font-size: 12px;
            color: #64748b;
            margin-bottom: 4px;
          }

          .activity-title {
            font-size: 14px;
            font-weight: 600;
            margin-bottom: 4px;
          }

          .activity-desc {
            font-size: 13px;
            color: #475569;
            line-height: 1.4;
          }

          /* Spinner */
          .spinner {
            width: 40px;
            height: 40px;
            margin: auto;
            border: 4px solid #e5e7eb;
            border-top: 4px solid #3b82f6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          /* No Data */
          .no-activities {
            grid-column: 1/-1;
            text-align: center;
            color: #64748b;
          }

          /* Sidebar Space - Desktop Only */
          .activities-wrapper {
            margin-left: 0;
          }

          @media (min-width: 1024px) {
            .activities-wrapper {
              margin-left: 260px;
              margin-bottom: 40px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default AllActivities;